/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Node.js SDK", description = "Hightouch's Node.js SDK for collecting events", lastUpdatedDate = new Date(1790035200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Node.js SDK",
        description: "Hightouch's Node.js SDK for collecting events",
        lastUpdatedDate: "Tue Sep 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Overriding messageId",
          "depth": 0,
          "id": "overriding-messageid"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      pre: "pre",
      code: "code",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      h3: "h3",
      strong: "strong"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Node.js SDK makes it easy to track events from Node.js applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsx(_components.p, {
        children: "The Node.js SDK is available via NPM."
      }), "\n", _jsx(_components.p, {
        children: "To install the Node.js SDK, run:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "npm install @ht-sdks/events-sdk-js-node\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "Initialize the SDK exactly once and export it:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-javascript",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEvents"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"@ht-sdks/events-sdk-js-node\""
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// initialize once"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " htevents = ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEvents"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"HT_WRITE_KEY\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "host"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://us-east-1.hightouch-events.com\""
          }), ",\n});\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Then, import it into the rest of your app, as needed:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-javascript",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "// import your already initialized instance into the rest of your app"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { htevents } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"./events\""
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// example usage in an express like framework"
          }), "\napp.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "post"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "'/login'"
          }), ", ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "req, res"
            }), ") =>"]
          }), " {\n   htevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "identify"
          }), "({\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": req.", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "userId"
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "previousId"
          }), ": req.", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "previousId"
          }), "\n  })\n  res.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "sendStatus"
          }), "(", _jsx(_components.span, {
            className: "hljs-number",
            children: "200"
          }), ")\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "HtEvents"
        }), " constructor accepts the following parameters:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "writeKey"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "Key that corresponds to your Hightouch source"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "host"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The base URL of the API. Default: \"", _jsx(_components.a, {
                href: "https://us-east-1.hightouch-events.com",
                children: "https://us-east-1.hightouch-events.com"
              }), "\""]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "path"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The API path route. Default: \"/v1/batch\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "maxRetries"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "The maximum number of attempts when flushing a batch. Default: 3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "maxEventsInBatch"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "The maximum number of messages to enqueue before flushing. Default: 15"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "The maximum amount of time (in milliseconds) to store events locally before forwarding to Hightouch. Defaults to 10000."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "httpRequestTimeout"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsx(_components.td, {
              children: "The maximum number of milliseconds to wait for an http request. Default: 10000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "disable"
              })
            }), _jsx(_components.td, {
              children: "Boolean"
            }), _jsx(_components.td, {
              children: "Disable the analytics library. All calls will be a noop. Default: false"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "identify"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the identify event to be sent. The second argument is an optional callback that's invoked after the event is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".identify"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "track"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "event"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Order completed\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "total"
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  }\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the track event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".track"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "page"
        }), " method sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "page"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the page event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".page"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "screen"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "category"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Docs\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the screen event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htevents", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".screen"
          }), "(message, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[callback]"
          }), ")\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsx(_components.td, {
              children: "Additional properties about the screen."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "group"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "groupId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"456\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "traits"
          }), ": {\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "company_location"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The first argument to the method is an object representing the group event to\nbe sent. The second argument is an optional callback that's invoked after the\nevent is sent."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["htevents.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(message, [callback])\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The message object may include the following fields:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymousId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["The user's anonymous ID. This is automatically set if both ", _jsx(_components.code, {
                children: "userId"
              }), " and ", _jsx(_components.code, {
                children: "anonymousId"
              }), " are omitted."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "context"
              }), ". By default, ", _jsx(_components.code, {
                children: "context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Date"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "overriding-messageid",
        children: "Overriding messageId"
      }), "\n", _jsxs(_components.p, {
        children: ["If you need control over the ", _jsx(_components.code, {
          children: "messageId"
        }), " format, you can override it using a ", _jsx(_components.code, {
          children: "before"
        }), " plugin. A ", _jsx(_components.code, {
          children: "before"
        }), " plugin runs before the event is sent and lets you mutate the event object directly:"]
      }), "\n", _jsx(_components.p, {
        children: "Example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEvents"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"@ht-sdks/events-sdk-js-node\""
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "export"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " htevents = ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "HtEvents"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"HT_WRITE_KEY\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "host"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://us-east-1.hightouch-events.com\""
          }), ",\n});\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "let"
          }), " seq = ", _jsx(_components.span, {
            className: "hljs-number",
            children: "0"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "generateMessageId"
          }), "(", _jsx(_components.span, {
            className: "hljs-params"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ", _jsxs(_components.span, {
            className: "hljs-string",
            children: ["`msg_", _jsxs(_components.span, {
              className: "hljs-subst",
              children: ["${", _jsx(_components.span, {
                className: "hljs-built_in",
                children: "Date"
              }), ".now().toString(", _jsx(_components.span, {
                className: "hljs-number",
                children: "36"
              }), ")}"]
            }), "_", _jsxs(_components.span, {
              className: "hljs-subst",
              children: ["${", _jsx(_components.span, {
                className: "hljs-built_in",
                children: "String"
              }), "(++seq).padStart(", _jsx(_components.span, {
                className: "hljs-number",
                children: "5"
              }), ", ", _jsx(_components.span, {
                className: "hljs-string",
                children: "\"0\""
              }), ")}"]
            }), "`"]
          }), ";\n}\n\nhtevents.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "register"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Custom MessageId\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"before\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "version"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1.0.0\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "isLoaded"
          }), ": ", _jsx(_components.span, {
            className: "hljs-function",
            children: "() =>"
          }), " ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "load"
          }), ": ", _jsx(_components.span, {
            className: "hljs-function",
            children: "() =>"
          }), " ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "Promise"
          }), ".", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "resolve"
          }), "(),\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "track"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "ctx"
            }), ") =>"]
          }), " {\n    ctx.", _jsx(_components.span, {
            className: "hljs-property",
            children: "event"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "messageId"
          }), " = ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "generateMessageId"
          }), "();\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ctx;\n  },\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "identify"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "ctx"
            }), ") =>"]
          }), " {\n    ctx.", _jsx(_components.span, {
            className: "hljs-property",
            children: "event"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "messageId"
          }), " = ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "generateMessageId"
          }), "();\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ctx;\n  },\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "page"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "ctx"
            }), ") =>"]
          }), " {\n    ctx.", _jsx(_components.span, {
            className: "hljs-property",
            children: "event"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "messageId"
          }), " = ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "generateMessageId"
          }), "();\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ctx;\n  },\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "group"
          }), ": ", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "ctx"
            }), ") =>"]
          }), " {\n    ctx.", _jsx(_components.span, {
            className: "hljs-property",
            children: "event"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "messageId"
          }), " = ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "generateMessageId"
          }), "();\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " ctx;\n  },\n});\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Add a handler for each event type you want to override, since a plugin only runs for the methods it defines. Only use alphanumeric characters, ", _jsx(_components.code, {
          children: "-"
        }), ", and ", _jsx(_components.code, {
          children: "_"
        }), " in your custom ", _jsx(_components.code, {
          children: "messageId"
        }), " values. Other special characters aren't supported."]
      })]
    });
  }
}
export default MDXContent;
